import { FormEvent, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import UserLayout from '../../../Component/UI/Layout_user';
import url from '../../../Component/server';
import DaumPostcodeEmbed from 'react-daum-postcode';

export interface MemberFields {
  nickname: string; name: string; user_email: string; phone: string;
  addr_num: string; addr_basic: string; addr_detail: string;
}
export type MemberSubmission = MemberFields & { terms_agree: 'y'; privacy_agree: 'y' };
interface Props {
  initialValues?: Partial<MemberFields>;
  ready: boolean;
  error?: string;
  onSubmit: (values: MemberSubmission) => Promise<void>;
  details?: boolean;
  initialTerms?: boolean;
  initialPrivacy?: boolean;
}
const empty: MemberFields = { nickname: '', name: '', user_email: '', phone: '', addr_num: '', addr_basic: '', addr_detail: '' };

// Ordinary Kakao signup and AWAKE signup share the same fields, layout and validation.
export default function MemberSignupForm({ initialValues, ready, error, onSubmit, details = false, initialTerms = false, initialPrivacy = false }: Props) {
  const [form, setForm] = useState<MemberFields>(empty);
  const [checkedNickname, setCheckedNickname] = useState('');
  const [nicknameMessage, setNicknameMessage] = useState('');
  const [checking, setChecking] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [manualAddress, setManualAddress] = useState(false);
  const [terms, setTerms] = useState(initialTerms), [privacy, setPrivacy] = useState(initialPrivacy);
  const [busy, setBusy] = useState(false), [formError, setFormError] = useState('');
  const saving = useRef(false), nicknameRequest = useRef(0);
  // Keep field visibility tied to the saved profile while the user fills missing details.
  const showField = (field: keyof MemberFields) => !details || !initialValues?.[field]?.trim();
  const kakaoFields = {
    name: !details && !!initialValues?.name?.trim(),
    user_email: !details && !!initialValues?.user_email?.trim(),
    phone: !details && !!initialValues?.phone?.trim(),
  };
  useEffect(() => {
    setForm(Object.fromEntries(Object.keys(empty).map(key => [key, String(initialValues?.[key as keyof MemberFields] || '')])) as unknown as MemberFields);
    setCheckedNickname(details ? initialValues?.nickname || '' : ''); setNicknameMessage('');
    nicknameRequest.current += 1;
  }, [initialValues, details]);
  const change = (key: keyof MemberFields, value: string) => {
    setForm(previous => ({ ...previous, [key]: value }));
    if (key === 'nickname') {
      nicknameRequest.current += 1;
      setCheckedNickname(''); setNicknameMessage(''); setChecking(false);
    }
  };
  const checkNickname = async () => {
    if (!form.nickname.trim() || Array.from(form.nickname).length < (details ? 1 : 2) || Array.from(form.nickname).length > (details ? 10 : 8)) {
      setNicknameMessage(details ? '닉네임은 1~10자로 입력해주세요.' : '닉네임은 2~8자로 입력해주세요.'); return;
    }
    if (details && form.nickname === initialValues?.nickname) {
      setCheckedNickname(form.nickname); setNicknameMessage('사용 가능한 닉네임입니다.'); return;
    }
    const request = ++nicknameRequest.current, nickname = form.nickname;
    setChecking(true); setCheckedNickname(''); setNicknameMessage('');
    try {
      const { data } = await axios.get(url.Communication_url.nick_dup_check, { params: { nickname } });
      if (request !== nicknameRequest.current) return;
      const available = data === '사용가능';
      setCheckedNickname(available ? nickname : '');
      setNicknameMessage(available ? '사용 가능한 닉네임입니다.' : '이미 존재하는 닉네임입니다.');
    } catch (err: any) {
      if (request === nicknameRequest.current) setNicknameMessage(err?.response?.status === 400 && err?.response?.data?.message === '중복'
        ? '이미 존재하는 닉네임입니다.' : '중복확인에 실패했습니다. 다시 시도해주세요.');
    } finally {
      if (request === nicknameRequest.current) setChecking(false);
    }
  };
  const isOk = ready && !busy && (details ? ['nickname', 'name', 'phone', 'addr_num', 'addr_basic'].every(key => form[key as keyof MemberFields].trim()) : Object.values(form).every(value => value.trim()))
    && checkedNickname === form.nickname && terms && privacy;
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!isOk || saving.current) return;
    saving.current = true; setBusy(true); setFormError('');
    try {
      await onSubmit({ ...form, phone: form.phone.replace(/-/g, ''), terms_agree: 'y', privacy_agree: 'y' });
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message;
      setFormError(Array.isArray(message) ? message.join(' ') : message || '저장하지 못했습니다. 다시 시도해주세요.');
    } finally { saving.current = false; setBusy(false); }
  };
  const content = <>
    <Form $details={details} onSubmit={submit} aria-label="회원정보" aria-busy={!ready || busy}>
      {!details && <HeaderTitle>회원정보</HeaderTitle>}
      {details && <DetailsIntro>구매에 필요한 정보를 입력해주세요. 내 정보에서 수정할 수 있습니다.</DetailsIntro>}
      {showField('nickname') && <>
        <Title htmlFor="signup-nickname">닉네임 <Required>*</Required></Title>
        <Row>
          <InputBox><Input id="signup-nickname" required maxLength={details ? 20 : 16} pattern={".*\\S.*"}
            placeholder="닉네임을 입력해주세요." value={form.nickname} disabled={!ready || busy}
            onChange={e => change('nickname', e.target.value)} /></InputBox>
          <Action type="button" disabled={!ready || busy || checking} onClick={checkNickname}>{checking ? '확인 중' : '중복확인'}</Action>
        </Row>
        <Notice>{details ? "닉네임은 1~10자로 입력해주세요." : "닉네임은 2~8자로 입력해주세요."}</Notice>
        {nicknameMessage && <Feedback role="status">{nicknameMessage}</Feedback>}
      </>}
      {showField('name') && <>
        <Title htmlFor="signup-name">이름 <Required>*</Required></Title>
        <InputBox><Input id="signup-name" required maxLength={10} autoComplete="name"
          readOnly={kakaoFields.name} aria-describedby={kakaoFields.name ? 'kakao-profile-notice' : undefined}
          placeholder="이름을 입력해주세요." value={form.name} disabled={!ready || busy}
          onChange={e => change('name', e.target.value)} /></InputBox>
      </>}
      {!details && <>
        <Title htmlFor="signup-email">이메일 <Required>*</Required></Title>
        <InputBox><Input id="signup-email" type="email" required maxLength={100} autoComplete="email"
          readOnly={kakaoFields.user_email} aria-describedby={kakaoFields.user_email ? 'kakao-profile-notice' : undefined}
          placeholder="이메일을 입력해주세요." value={form.user_email} disabled={!ready || busy}
          onChange={e => change('user_email', e.target.value)} /></InputBox>
      </>}
      {showField('phone') && <>
        <Title htmlFor="signup-phone">연락처 <Required>*</Required></Title>
        <InputBox><Input id="signup-phone" type="tel" required maxLength={16} pattern="0[0-9\-]{8,15}" autoComplete="tel"
          readOnly={kakaoFields.phone} aria-describedby={kakaoFields.phone ? 'kakao-profile-notice' : undefined}
          placeholder="010-1234-5678" value={form.phone} disabled={!ready || busy}
          onChange={e => change('phone', e.target.value)} /></InputBox>
      </>}
      {Object.values(kakaoFields).some(Boolean) && <Feedback id="kakao-profile-notice">카카오에서 제공받은 이름·이메일·연락처는 수정할 수 없습니다. 제공되지 않은 정보는 직접 입력해주세요.</Feedback>}
      {(showField('addr_num') || showField('addr_basic')) && <>
        <Title htmlFor="signup-address">주소 <Required>*</Required></Title>
        <Row>
          <InputBox><Input readOnly={!manualAddress} required={manualAddress} maxLength={5} pattern="[0-9]{5}" inputMode="numeric"
            aria-label="우편번호" placeholder="우편번호" value={form.addr_num} onChange={e => change('addr_num',e.target.value)} /></InputBox>
          <Action type="button" disabled={!ready || busy} aria-expanded={searchOpen} onClick={() => setSearchOpen(true)}>검색</Action>
        </Row>
        {searchOpen && <AddressSearch aria-label="주소 검색">
          <ManualAddress type="button" onClick={() => { setManualAddress(true); setSearchOpen(false); }}>검색이 안 되나요? 주소 직접 입력</ManualAddress>
          <button type="button" onClick={() => setSearchOpen(false)}>주소 검색 닫기</button>
          <DaumPostcodeEmbed style={{ height: 420 }}
            errorMessage={<p role="alert">주소 검색을 불러오지 못했습니다. 닫은 후 다시 시도해주세요.</p>}
            onComplete={address => {
              setForm(previous => ({ ...previous, addr_num: address.zonecode, addr_basic: address.address, addr_detail: '' }));
              setManualAddress(false);
              setSearchOpen(false);
            }} />
        </AddressSearch>}
        <InputBox><Input id="signup-address" readOnly={!manualAddress} required={manualAddress} maxLength={255}
          placeholder="주소" value={form.addr_basic} onChange={e => change('addr_basic',e.target.value)} /></InputBox>
        <InputBox><Input aria-label="상세주소" required={!details} maxLength={255} autoComplete="address-line2"
          placeholder="상세주소" value={form.addr_detail} disabled={!ready || busy}
          onChange={e => change('addr_detail', e.target.value)} /></InputBox>
      </>}
      {(!details || !initialTerms || !initialPrivacy) && <Agreements>
        {(!details || !initialTerms) && <Agreement><input type="checkbox" required checked={terms} disabled={!ready || busy} onChange={e => setTerms(e.target.checked)} />
          <span><a href="/rule/1" target="_blank" rel="noreferrer">이용약관</a> 동의 (필수)</span></Agreement>}
        {(!details || !initialPrivacy) && <Agreement><input type="checkbox" required checked={privacy} disabled={!ready || busy} onChange={e => setPrivacy(e.target.checked)} />
          <span><a href="/rule/2" target="_blank" rel="noreferrer">개인정보처리방침</a> 동의 (필수)</span></Agreement>}
      </Agreements>}
      {(error || formError) && <Notice role="alert">{error || formError}</Notice>}
      <SaveButton type="submit" disabled={!isOk}>{busy ? '저장 중...' : '저장하기'}</SaveButton>
    </Form>
  </>;
  return details ? content : <UserLayout background="white">{content}</UserLayout>;
}

const Form = styled.form<{ $details: boolean }>`padding: ${({ $details }) => $details ? '20px 24px 24px' : '0 24px 40px'};`;
const DetailsIntro = styled.p`
  margin: 0; font-size: 14px; line-height: 1.5; color: var(--user-text-muted);
`;
const HeaderTitle = styled.h1`
  font-size: 20px; font-family: var(--font-Pretendard-Bold);
  text-align: center; color: var(--user-text); margin: 40px 0 24px;
`;
const Title = styled.label`
  display: block; font-size: 14px; font-family: var(--font-Pretendard-Medium);
  color: var(--user-text); margin-top: 24px;
`;
const Required = styled.span`color: var(--user-error);`;
const Row = styled.div`display: flex; justify-content: space-between; width: 100%; gap: 12px;`;
const InputBox = styled.div`
  display: flex; background: var(--user-surface); border-radius: 8px; border: 1px solid var(--user-border);
  width: 100%; min-width: 0; height: 50px; align-items: center; padding: 12px 16px;
  box-sizing: border-box; margin-top: 8px;
`;
const Input = styled.input`
  font-size: 15px; color: var(--user-text); background: transparent; border: none; width: 100%; min-width: 0;
  &:focus-visible { outline: 2px solid var(--user-accent); outline-offset: 3px; }
  &::placeholder { color: var(--user-text-muted); }
  &:read-only { color: var(--user-text-muted); cursor: default; }
  &:-webkit-autofill { -webkit-box-shadow: 0 0 0 1000px white inset !important; }
`;
const Action = styled.button`
  flex: 0 0 24%; cursor: pointer; background: #2d2d2d; height: 50px; margin-top: 8px;
  border: 0; border-radius: 8px; padding: 0 8px; font-size: 15px;
  font-family: var(--font-Pretendard-Medium); color: white;
  &:active { background: #1a1a1a; }
  &:disabled { opacity: .5; cursor: default; }
`;
const Notice = styled.div`
  font-size: 12px; color: var(--user-error); margin-top: 8px; text-align: left;
  font-family: var(--font-Pretendard-Regular); line-height: 16px;
`;
const Feedback = styled.div`font-size: 12px; margin-top: 8px; line-height: 16px;`;
const ManualAddress = styled.button`display: block; border: 0; background: transparent; padding: 8px; min-height: 44px; font-size: 13px; color: var(--user-accent); text-decoration: underline; cursor: pointer;`;
const AddressSearch = styled.section`
  margin-top: 12px; border: 1px solid var(--user-border); border-radius: 8px; overflow: hidden;
  > button { display: block; margin: 8px 8px 8px auto; padding: 8px; border: 0; background: var(--user-surface-raised); color: var(--user-text); border-radius: 4px; min-height: 44px; cursor: pointer; }
`;
const Agreements = styled.div`display: grid; gap: 12px; margin-top: 24px;`;
const Agreement = styled.label`
  display: flex; gap: 8px; align-items: center; font-size: 13px;
  input { accent-color: #7955f0; width: 16px; height: 16px; }
  a { color: inherit; text-decoration: underline; }
`;
const SaveButton = styled.button`
  display: flex; margin-top: 32px; width: 100%; height: 50px; border: 0;
  background: linear-gradient(135deg, #7955f0, #3209b9); border-radius: 8px; color: white;
  align-items: center; justify-content: center; font-size: 16px;
  font-family: var(--font-Pretendard-Bold); cursor: pointer;
  &:disabled { background: var(--user-surface-raised); color: var(--user-text-muted); cursor: default; }
  &:active:not(:disabled) { opacity: .8; }
`;
